import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';

const features = [
  ['01', 'Physical robotic arm', 'Moves real chess pieces with millimetre precision — not a screen, not a simulation. This is what stops passengers in their tracks.'],
  ['02', '25 AI difficulty levels', 'From ELO 200 — suitable for a complete beginner — to ELO 2900, approaching the strength of a world champion. Every passenger finds their level.'],
  ['03', '1,200+ structured exercises', 'Plus 145 endgame challenges and 100 annotated master games. A complete chess education built into a single device.'],
  ['04', 'Voice coaching', 'Interactive spoken feedback and learning prompts — a tireless tutor available whenever the instructor is occupied elsewhere.'],
  ['05', 'Beginner-friendly', 'Auto-detects and explains illegal moves in real time. Ideal for first-time players learning the rules — patient, consistent, non-judgemental.'],
  ['06', 'Online play via Lichess', 'Integrates with Lichess.org — passengers can play real opponents anywhere in the world using the physical board. Subject to ship WiFi availability.'],
];

const boards = [
  {
    name: 'The King',
    type: 'Electronic Auto-Playing Board',
    desc: 'A self-contained electronic board allowing passengers to play against a computer opponent at adjustable difficulty levels, independently and at any time.',
    uses: ['Independent practice between sessions', 'Drop-in play in the chess corner', 'Head-to-head with computer arbitration'],
  },
  {
    name: 'Chessnut Evo',
    type: 'Premium Electronic Board',
    desc: 'A premium electronic board with piece recognition and app connectivity — ideal for coached games, demonstrations, and independent passenger play at a high standard.',
    uses: ['Coached game demonstrations', 'Opening and endgame analysis', 'Connected app play'],
  },
  {
    name: 'GoChess Classic',
    type: 'Electronic Auto-Playing Board',
    desc: 'A further auto-playing board providing additional capacity for passenger use — ensuring that demand is never turned away, even during busy sea days.',
    uses: ['Additional drop-in capacity', 'Simultaneous sessions', 'Tournament support'],
  },
];

const alsoIncluded = [
  '20 tournament-standard chess sets with weighted pieces — sufficient for a full onboard simultaneous or tournament',
  'Roll-up vinyl boards for travel and flexible setup in any lounge or library space',
  'Professional magnetic demonstration board for group workshop sessions — visible across a large room',
  'All equipment PAT tested prior to embarkation — certificates available for inspection',
];

const equipment = [
  'SenseRobot AI Chess System', 'The King Electronic Board', 'Chessnut Evo',
  'GoChess Classic', '20 × Tournament Chess Sets', 'Roll-Up Boards', 'Magnetic Demo Board',
];

export default function Technology() {
  return (
    <>
      <PageHero
        tag="Technology"
        title={<>Equipment that creates <em>moments</em></>}
        lede="Professional-grade chess technology that passengers have never encountered before — all owned, transported, and managed by the instructor at zero cost to the cruise line."
      />

      <section className="senserobot on-dark">
        <div className="inner">
          <Reveal>
            <p className="section-tag">The Centrepiece</p>
            <h2 className="section-title">The SenseRobot <em>AI Chess System</em></h2>
            <div className="divider" />
            <p className="section-body">
              Showcased at CES 2025. Adopted by leading chess organisations worldwide. The world&rsquo;s
              first fully-automated AI chess robot with a physical robotic arm that moves real pieces on
              a real board. Not a screen simulation. Not a digital display. A robot that physically
              plays chess — in front of you.
            </p>
          </Reveal>
          <div className="sr-grid">
            <Reveal className="sr-visual">
              <div className="sr-badge">World First</div>
              <span className="sr-robot-icon">♜</span>
              <p>“A robot arm with millimetre-level precision reaches out, picks up a chess piece, and places it on its new square. The board is real. The pieces are real. The arm is real. Most passengers will never have seen anything like it.”</p>
            </Reveal>
            <Reveal className="sr-features" delay={120}>
              {features.map(([n, h, p]) => (
                <div className="sr-feat" key={n}>
                  <div className="sr-feat-num">{n}</div>
                  <div><h4>{h}</h4><p>{p}</p></div>
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      <section className="boards">
        <div className="inner">
          <Reveal>
            <p className="section-tag">The Full Equipment Package</p>
            <h2 className="section-title">Every board. <em>Every need.</em></h2>
            <div className="divider" />
            <p className="section-body">
              Beyond the SenseRobot, the Chess at Sea equipment package includes three further
              electronic boards and a full complement of tournament sets — providing persistent chess
              access throughout the voyage for every passenger.
            </p>
          </Reveal>
          <div className="boards-grid">
            {boards.map((b, i) => (
              <Reveal key={b.name} className="board-card" delay={i * 90}>
                <div className="board-name">{b.name}</div>
                <div className="board-type">{b.type}</div>
                <p className="board-desc">{b.desc}</p>
                <ul className="board-uses">
                  {b.uses.map((u) => <li key={u}>{u}</li>)}
                </ul>
              </Reveal>
            ))}
          </div>
          <Reveal className="also-included">
            <div>
              <p className="ai-label">Also included</p>
              <h3>Tournament Sets &amp; Teaching Equipment</h3>
            </div>
            <div className="ai-grid">
              {alsoIncluded.map((t) => (
                <div key={t}><span className="glyph">♟</span><span className="t">{t}</span></div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="compliance">
        <div className="inner">
          <Reveal>
            <p className="section-tag">Compliance &amp; Safety</p>
            <h2 className="section-title">Everything <em>in order</em></h2>
            <div className="divider" />
            <p className="section-body">
              All electrical equipment is PAT tested before every voyage. Certificates are carried and
              available for inspection on request.
            </p>
          </Reveal>
          <div className="comp-grid">
            <Reveal className="comp-card">
              <h3>PAT Testing</h3>
              <p>All electronic chess equipment — the SenseRobot, The King, Chessnut Evo, and GoChess Classic — is fully PAT tested prior to embarkation. Current PAT test certificates are carried for inspection. This is standard practice and is completed before every voyage.</p>
            </Reveal>
            <Reveal className="comp-highlight" delay={120}>
              <h3>WiFi Requirement</h3>
              <p>The SenseRobot&rsquo;s full functionality — specifically online play via Lichess.org and over-the-air software updates — requires a WiFi connection. Access to the ship&rsquo;s WiFi network for the SenseRobot unit is requested. All other electronic boards operate fully independently without WiFi.</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="zero-cost">
        <Reveal>
          <h2>Zero equipment cost <em>to the line.</em></h2>
          <p>
            Every item in the Chess at Sea equipment package is owned, transported, insured, and
            managed by the instructor. The cruise line procures nothing, stores nothing, and
            maintains nothing.
          </p>
          <div className="equipment-list">
            {equipment.map((e) => <span className="equip-item" key={e}>{e}</span>)}
          </div>
        </Reveal>
      </section>

      <section className="cta-band deep">
        <Reveal>
          <h2>Interested in <em>Chess at Sea?</em></h2>
          <p>Get in touch to discuss how the programme — and the technology — can work for your passengers.</p>
          <Link to="/about#contact" className="btn-primary">Enquire Now <span className="arrow">→</span></Link>
        </Reveal>
      </section>
    </>
  );
}
